import Footer from "../components/Footer";
import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import Sidebar from "../components/sidebar/Sidebar";
import {
  CssBaseline,
  Paper as MuiPaper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CircularProgress, Box } from "@mui/material";
import { spacing } from "@mui/system";
import VerificationAlert from "components/auth/VerificationAlert";
import { PageBreadcrumbProvider } from "components/ui/PageBreadcrumbContext";
import { SubscriptionContextProvider } from "contexts/SubscriptionContext";
import useAuth from "hooks/useAuth";
import { useEffect, useState, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import styled from "styled-components/macro";

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100dvh;
`;

const DrawerContainer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) => (props.open ? drawerWidth : 0)}px;
    transition: ${({ theme }) =>
      theme.transitions.create("width", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      })};
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};
  overflow-y: auto;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Loader = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
    }}
  >
    <CircularProgress />
  </Box>
);
const dashboardPathRegex = /^\/dashboards\/([a-f0-9-]+)\/edit$/;
const dashboardComponentPathRegex =
  /^\/dashboardComponents\/([a-f0-9-]+)\/edit$/;
const edidDetailsPathRegex = /^\/eventdevice\/([a-f0-9-]+)$/;
const liveStreamPathRegex = /^\/admin\/eventDevices\/([a-f0-9-]+)\/liveStream$/;

const Dashboard = ({ children }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [drawerOpen, setDrawerOpen] = useState(true);
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // This is a temporary workaround to remove padding and close drawer
  // by default for dashboard editor page
  const { pathname } = useLocation();

  const noPadding = [dashboardComponentPathRegex, dashboardPathRegex].some(
    (regex) => regex.test(pathname)
  );
  const hideDrawer = [
    dashboardPathRegex,
    dashboardComponentPathRegex,
    edidDetailsPathRegex,
    liveStreamPathRegex,
  ].some((regex) => regex.test(pathname));

  useEffect(() => {
    if (hideDrawer) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(isDesktop);
    }
  }, [hideDrawer, isDesktop, pathname]);

  return (
    <SubscriptionContextProvider>
      <Root>
        <CssBaseline />
        <GlobalStyle />
        <DrawerContainer open={drawerOpen}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            ModalProps={{ keepMounted: true }}
            variant={isDesktop ? "persistent" : "temporary"}
            open={drawerOpen}
            onClose={handleDrawerToggle}
          />
        </DrawerContainer>
        <AppContent>
          {!user.verified && <VerificationAlert />}
          <PageBreadcrumbProvider>
            <Navbar onDrawerToggle={handleDrawerToggle} />
            <MainContent
              px={noPadding ? 0 : { xs: 2, sm: 4, md: 6 }}
              py={noPadding ? 0 : { xs: 2, sm: 4, md: 6 }}
            >
              {children}
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </MainContent>
          </PageBreadcrumbProvider>
          <Footer />
        </AppContent>
      </Root>
    </SubscriptionContextProvider>
  );
};

export default Dashboard;
